import React, {forwardRef, useContext, useRef, useState, useEffect} from "react";
import {useRouter} from "next/router";
import Layout from "../../../components/layouts/EmptyLayout";
import useSWR, {mutate} from "swr";
import {Box, CheckBox, Grid, Heading, InfiniteScroll, List, ResponsiveContext, Text, ThemeContext} from "grommet";
import {NotificationsContext} from "../../../components/NotificationsProvider";
import {handleErrors, handleFileDownloadErrors} from "../../../utils/errors";
import {formatCurrency} from "../../../utils/numbers";
import {formatDate} from "../../../utils/dates";
import Checkout from "../../../components/checkout";
import {filterAttributeInObject} from "../../../utils/object";
import {useReactToPrint} from "react-to-print";
import Loading from "../../../components/Loading";
import {VisibleOnlyInPrint} from "../../../utils/print";
import DataTable from "react-data-table-component";
import Badge from "../../../components/Badge";
import {getInvoiceStatusBadge, getOverdueRange} from "../../../utils/invoice";
import {OVERDUE_COLOR_MAP} from "../../../constants/Colors";
import {normalizeColor} from "grommet/utils";
import {formatName} from "../../../utils/names";
import MerchantHeader from "../../../components/MerchantHeader";
import PoweredByStreamlinedFooter from "../../../components/PoweredByStreamlinedFooter";
import {
    faChevronLeft as IconChevronLeft,
    faArrowUpRightFromSquare as IconExpand,
    faEllipsisStrokeVertical,
} from "@fortawesome/pro-light-svg-icons";
import { downloadBlobAsCsvFile } from "../../../lib/exportcsv";
import FontAwesomeIcon from "../../../components/FontAwesomeIcon";
import AddPaymentModal from "../../../components/pages/customers/AddPaymentModal";
import Button from "../../../components/Button";
import Menu from "../../../components/Menu";
import RefundBankLinkModal from "../../../components/RefundBankLinkModal";

const Statement = () => {
    const router = useRouter();
    const { uuid, refund_bank_link_token } = router.query;
    const notificationCtx = useContext(NotificationsContext);
    const [mounted, setMounted] = useState(false);
    const [selectedPastDueInvoices, setSelectedPastDueInvoices] = useState({});
    const [selectedDueLaterInvoices, setSelectedDueLaterInvoices] = useState({});
    const [allInvoices, setAllInvoices] = useState();
    const [isCheckout, setIsCheckout] = useState(false);
    const [isShowingPastInvoice, setIsShowingPastInvoice] = useState(false);
    const [hasOutstandingInvoice, setHasOutstandingInvoice] = useState(false);
    const [showAddPaymentModal, setShowAddPaymentModal] = useState();
    const [notFoundError, setNotFoundError] = useState(false);
    const [showRefundBankLinkModal, setShowRefundBankLinkModal] = useState(false);
    const [refundBankLinkToken, setRefundBankLinkToken] = useState(null);
    const printRef = useRef();

    // Prevent hydration mismatch from ResponsiveContext (server doesn't know viewport size)
    useEffect(() => setMounted(true), []);

    // Show refund bank link modal if token is in URL
    useEffect(() => {
        if (refund_bank_link_token) {
            setRefundBankLinkToken(refund_bank_link_token);
            setShowRefundBankLinkModal(true);
        }
    }, [refund_bank_link_token]);

    useEffect(() => {
        if (notFoundError) {
            router.push('/statement-unavailable');
        }
    }, [notFoundError, router]);

    const fetcher = (url) =>
        fetch(url, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
            },
        })
            .then(handleErrors)
            .catch(err => {
                if (err.status === 404) {
                    setNotFoundError(true);
                    return;
                }
                return notificationCtx.showNotification(err);
            });

    // Read inside the SWR callback, which closes over the render it was created in.
    const isCheckoutRef = useRef(false);
    useEffect(() => {
        isCheckoutRef.current = isCheckout;
    }, [isCheckout]);

    const { data, error } = useSWR(uuid ? `/api/statement/${uuid}` : null, fetcher, {
        revalidateOnFocus: false,
        onSuccess: (data) => {
            /*
                Never re-derive the selection while a checkout is open. SWR still
                revalidates on reconnect, and mutate() is called from a couple of places,
                so this callback can fire with a live PaymentIntent already priced off the
                current selection. Swapping the token set underneath the open form is
                precisely how a customer gets charged for one set of invoices while a
                different set is recorded as paid. The freeze covers `allInvoices` and
                `hasOutstandingInvoice` too: the first feeds token lookup, and the second
                gates the widget that hosts the in-flight payment.
            */
            if (!isCheckoutRef.current) {
                const dueNowMap = {};
                data?.past_due_invoices && data.past_due_invoices.map(invoice => dueNowMap[invoice?.invoice_token] = true);
                setSelectedPastDueInvoices(dueNowMap);

                const dueLaterMap = {};
                data?.not_yet_due_invoices && data.not_yet_due_invoices.map(invoice => dueLaterMap[invoice?.invoice_token] = true);
                setSelectedDueLaterInvoices(dueLaterMap);

                setAllInvoices([...(data?.past_due_invoices || []), ...(data?.not_yet_due_invoices || [])]);
                setHasOutstandingInvoice((data?.past_due_invoices || []).length > 0 || (data?.not_yet_due_invoices || []).length > 0);
            }

            // Check if there's a pending refund that needs bank linking
            if (data?.pending_refund_bank_link?.token && !refund_bank_link_token) {
                setRefundBankLinkToken(data.pending_refund_bank_link.token);
                setShowRefundBankLinkModal(true);
            }
        }
    });

    const getSelectedInvoices = (invoices) => {
        const filteredInvoices = filterAttributeInObject(invoices, val => !!val);
        // filter(Boolean): a selected token with no matching invoice would otherwise come
        // back as undefined, then map to an undefined invoice_token and silently poison
        // the token list we charge and confirm against.
        return (Object.keys(filteredInvoices))
            .map(token => (allInvoices || []).find(obj => obj?.invoice_token === token))
            .filter(Boolean);
    }

    const getCheckoutTotal = () => {
        const invoices = getSelectedInvoices({...selectedPastDueInvoices, ...selectedDueLaterInvoices});
        // using Math.max because we don't want any negative balances
        return invoices.reduce((acc, current) => acc + (Math.max(0, current.open_balance - (current.total_pending_payment || 0))), 0);
    }

    if (error) {
        return (
            <Layout title="Error">
                <Box align="center" pad="large">
                    <Text>An error occurred while loading the statement. Please try again later.</Text>
                </Box>
            </Layout>
        );
    }

    // Wait for client mount to avoid ResponsiveContext hydration mismatch
    if (!mounted) {
        return (
            <Layout title="Loading">
                <Box align="center" pad="large">
                    <Loading />
                </Box>
            </Layout>
        );
    }

    return data ? (
        <Layout title="Account statement">
            <Box align="center">
                <ResponsiveContext.Consumer>
                    {(size) => (
                        <Box pad={{ vertical: "large", horizontal: "medium" }} width="xlarge">
                            <MerchantHeader data={data?.merchant} />
                            <CustomerSummaryHeader
                                data={data?.customer}
                                printRef={printRef}
                                setShowAddPaymentModal={setShowAddPaymentModal}
                                uuid={uuid}
                            />
                            <Box border={{ side: "top", color: "border-strong" }} pad={{ vertical: "medium" }}>
                                <Grid
                                    row={["auto"]}
                                    columns={size === "small" ? ["auto"] : ["65%", "35%"]}
                                    gap={size === "small" ? "small" : "medium"}
                                >
                                    <Box>
                                        {isCheckout ? (
                                            <Box animation={["slideUp", "fadeIn"]} pad={{ top: "small" }}>
                                                <InvoiceListSection
                                                    invoices={getSelectedInvoices({
                                                        ...selectedPastDueInvoices,
                                                        ...selectedDueLaterInvoices,
                                                    })}
                                                    title="Checkout summary"
                                                    isCheckoutSummary
                                                    setIsCheckout={setIsCheckout}
                                                />
                                            </Box>
                                        ) : (
                                            <StatementOfAccountBody
                                                data={data}
                                                uuid={uuid}
                                                selectedPastDueInvoices={selectedPastDueInvoices}
                                                setSelectedPastDueInvoices={setSelectedPastDueInvoices}
                                                selectedDueLaterInvoices={selectedDueLaterInvoices}
                                                setSelectedDueLaterInvoices={setSelectedDueLaterInvoices}
                                                isCheckout={isCheckout}
                                                isShowingPastInvoice={isShowingPastInvoice}
                                                setIsShowingPastInvoice={setIsShowingPastInvoice}
                                                selectedInvoices={getSelectedInvoices({
                                                    ...selectedPastDueInvoices,
                                                    ...selectedDueLaterInvoices,
                                                })}
                                                hasOutstandingInvoice={hasOutstandingInvoice}
                                            />
                                        )}
                                    </Box>
                                    <Box
                                        pad={size === "small" ? {} : { horizontal: "medium", vertical: "medium" }}
                                        margin={size === "small" ? {} : { top: "medium" }}
                                    >
                                        {!isShowingPastInvoice && hasOutstandingInvoice && (
                                            <>
                                                <BulkPaymentWidget
                                                    selectedInvoices={getSelectedInvoices({
                                                        ...selectedPastDueInvoices,
                                                        ...selectedDueLaterInvoices,
                                                    })}
                                                    uuid={uuid}
                                                    isCheckout={isCheckout}
                                                    setIsCheckout={setIsCheckout}
                                                    recipientEmailAddress={data?.customer.email}
                                                    checkoutTotal={getCheckoutTotal()}
                                                    currency={data?.currency}
                                                    merchant={data?.merchant}
                                                    onReturnToMainMenu={() => {
                                                        setIsCheckout(false);
                                                        if (uuid) {
                                                            mutate(`/api/statement/${uuid}`);
                                                        }
                                                    }}
                                                />
                                                <Box fill="horizontal" pad={{ vertical: "small" }}>
                                                    <PoweredByStreamlinedFooter />
                                                </Box>
                                            </>
                                        )}
                                    </Box>
                                </Grid>
                            </Box>
                        </Box>
                    )}
                </ResponsiveContext.Consumer>
                {showAddPaymentModal && (
                    <AddPaymentModal
                        onClose={() => setShowAddPaymentModal()}
                        hideEmailOptions
                        customerUUID={data?.recipient_uuid}
                        customerEmail={data?.customer.email}
                        title={data?.customer?.quick_pay_enrolled ? "Edit QuickPay preferences" : "Set up QuickPay"}
                        isCustomerRequest={true}
                        bodyText={`A one-time secure link will be sent to ${data?.customer.email} to ${data?.customer?.quick_pay_enrolled ? "edit QuickPay preferences" : "set up QuickPay"}.`}
                    />
                )}
                {showRefundBankLinkModal && refundBankLinkToken && (
                    <RefundBankLinkModal
                        customerUuid={uuid}
                        token={refundBankLinkToken}
                        onClose={() => {
                            setShowRefundBankLinkModal(false);
                            // Remove token from URL without reload
                            if (refund_bank_link_token) {
                                const { refund_bank_link_token: _, ...query } = router.query;
                                router.replace({ pathname: router.pathname, query }, undefined, { shallow: true });
                            }
                        }}
                        onSuccess={() => {
                            // Refresh statement data after successful bank linking
                            mutate(`/api/statement/${uuid}`);
                        }}
                    />
                )}
            </Box>
            <VisibleOnlyInPrint>
                <PrintedStatementOfAccount ref={printRef} data={data} />
            </VisibleOnlyInPrint>
        </Layout>
    ) : (
        <Layout title="Loading">
            <Box align="center" pad="large">
                <Loading />
            </Box>
        </Layout>
    );
};

const PrintedStatementOfAccount = forwardRef(({data}, ref) => {
    const theme = useContext(ThemeContext);
    const columns = [
        {
            selector: row => row.formatted_created_at,
            name: 'Invoice Date',
            sortable: true,
            sortField: 'formatted_created_at',
            cell: (datum) => formatDate(datum?.issued_at || datum?.created_at),
        },
        (data?.customer?.type === 'group' && {
            selector: row => row.customer,
            name: 'Customer',
            sortable: true,
            sortField: 'customer',
            cell: (datum) => datum.recipient.display_name
        }),
        {
            selector: row => row.formatted_due_date,
            name: 'Due Date',
            sortable: true,
            sortField: 'due_date',
            cell: (datum) => formatDate(datum.due_date)
        },
        {
            selector: row => row.invoice_number,
            name: 'Invoice #',
            cell: (datum) => datum.order?.identifier,
            sortField: 'invoice_number',
            sortable: true,
        },
        {
            selector: row => row.amount,
            name: 'Amount',
            right: true,
            sortable: true,
            type: "currency",
            sortField: 'amount',
            cell: (datum) => formatCurrency(datum.open_balance, datum.currency)
        },
        {
            selector: row => row.open_balance,
            name: 'Balance Due',
            right: true,
            sortable: true,
            type: "currency",
            sortField: 'open_balance',
            cell: (datum) => formatCurrency(datum.open_balance, datum.currency)
        },
        {
            selector: row => row.invoice_status,
            name: 'Status',
            grow: 1.3,
            cell: datum => getInvoiceStatusBadge({...datum, theme})
        }
    ].filter(Boolean);
    const pastDue =  (data?.past_due_invoices || []).map(invoice => ({...invoice, invoice_status: 'Overdue'}));
    const notYetDue =  (data?.not_yet_due_invoices || []).map(invoice => ({...invoice, invoice_status: 'Not yet due'}));
    return (
        <Box ref={ref}>
            <MerchantHeader data={data?.merchant} />
            <CustomerSummaryHeader data={data?.customer}/>
            <Box border={{side: 'top'}}>
                <DataTable
                    data={([...pastDue, ...notYetDue]).map(
                        invoice => ({
                            ...invoice,
                            overdue_range: getOverdueRange(new Date(invoice.due_date))
                        })
                    )}
                    dense
                    columns={columns}
                    isServer={false}
                    titleProps={{margin: {top: '0'}}}
                    dataTableWrapperProps={{margin: "0"}} />
            </Box>
        </Box>
    );
})

const PAGE_SIZE = 20;

const StatementOfAccountBody = ({
    data,
    uuid,
    selectedPastDueInvoices,
    setSelectedPastDueInvoices,
    selectedDueLaterInvoices,
    setSelectedDueLaterInvoices,
    isShowingPastInvoice,
    setIsShowingPastInvoice,
    hasOutstandingInvoice
}) => {
    const notificationCtx = useContext(NotificationsContext);
    const [closedInvoices, setClosedInvoices] = useState([]);
    const [closedInvoicesOffset, setClosedInvoicesOffset] = useState(0);
    const [closedInvoicesTotal, setClosedInvoicesTotal] = useState(0);
    const [hasMoreClosedInvoices, setHasMoreClosedInvoices] = useState(true);
    const [isInitialLoad, setIsInitialLoad] = useState(true);
    const loadedIds = useRef(new Set());

    // Reset state when leaving closed invoices view
    useEffect(() => {
        if (!isShowingPastInvoice) {
            setClosedInvoices([]);
            setClosedInvoicesOffset(0);
            setClosedInvoicesTotal(0);
            setHasMoreClosedInvoices(true);
            setIsInitialLoad(true);
            loadedIds.current.clear();
        }
    }, [isShowingPastInvoice]);

    const fetcher = (url) =>
        fetch(url, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
            },
        })
            .then(handleErrors)
            .catch(notificationCtx.showNotification);

    const { data: closedInvoicesData, isLoading: isLoadingClosedInvoices } = useSWR(
        (isShowingPastInvoice || !hasOutstandingInvoice) && uuid
            ? `/api/statement/paid/${uuid}?offset=${closedInvoicesOffset}&limit=${PAGE_SIZE}`
            : undefined,
        fetcher, {
            revalidateOnFocus: false,
        }
    );

    // Accumulate closed invoices as they load
    useEffect(() => {
        if (!closedInvoicesData || !isShowingPastInvoice) return;

        const { paid: newInvoices, total } = closedInvoicesData;

        if (total !== undefined) {
            setClosedInvoicesTotal(total);
        }

        if (newInvoices && newInvoices.length > 0) {
            const uniqueNewInvoices = newInvoices.filter(inv => !loadedIds.current.has(inv.id));
            uniqueNewInvoices.forEach(inv => loadedIds.current.add(inv.id));
            setClosedInvoices(prev => [...prev, ...uniqueNewInvoices]);
        }

        const currentCount = closedInvoices.length + (newInvoices?.length || 0);
        setHasMoreClosedInvoices(currentCount < (total || 0));
        setIsInitialLoad(false);
    }, [closedInvoicesData, isShowingPastInvoice]);

    const loadMoreClosedInvoices = () => {
        if (!isLoadingClosedInvoices && hasMoreClosedInvoices) {
            setClosedInvoicesOffset(prev => prev + PAGE_SIZE);
        }
    };

    const renderInvoiceSelectionList = () => {
        if (isShowingPastInvoice)
            return (isInitialLoad && closedInvoices.length === 0) ? (<Loading />) : (
                <Box animation={["slideUp", "fadeIn"]}>
                    <InvoiceListSection
                        invoices={closedInvoices}
                        isGroup={data?.customer?.type && data?.customer?.type === 'group'}
                        isClosedInvoices={true}
                        hasMoreClosedInvoices={hasMoreClosedInvoices}
                        onLoadMore={loadMoreClosedInvoices}
                        isLoadingMore={isLoadingClosedInvoices}
                        totalClosedInvoices={closedInvoicesTotal}
                        navigationLink={(
                            <Box
                                direction="row"
                                align="center"
                                justify="between"
                                fill="horizontal">
                                <Button
                                    event={{section: 'statement_of_account_page_past_invoice', element: 'back'}}
                                    label={(
                                        <Box direction="row" align="center" gap="small">
                                            <FontAwesomeIcon SelectedIcon={IconChevronLeft} size="small" />
                                            <Text>Back</Text>
                                        </Box>
                                    )}
                                    onClick={() => setIsShowingPastInvoice(false)}
                                    hoverIndicator/>
                            </Box>
                        )}
                    />
                </Box>
            );
        return (
            <Box animation={{type: 'slideDown', size: 'xsmall'}} pad={{top: 'small'}}>
                <Box animation="fadeIn">
                    {data?.past_due_invoices && data?.past_due_invoices.length === 0 && data?.not_yet_due_invoices && data?.not_yet_due_invoices.length === 0 && (
                        <Box pad={{vertical: 'small'}}>
                            <Text>You have no outstanding invoices.</Text>
                        </Box>
                    )}
                    {data?.past_due_invoices && data?.past_due_invoices.length > 0 ?
                        (<InvoiceListSection
                            title="Due now"
                            isGroup={data?.customer?.type && data?.customer?.type === 'group'}
                            invoices={data.past_due_invoices}
                            checkboxAttr='due-now'
                            selectedInvoices={selectedPastDueInvoices}
                            onCheck={() => setSelectedPastDueInvoices({...selectedPastDueInvoices})}
                            wrapperProps={{margin: {bottom: 'medium'}}}
                        />) :
                        (<></>)}
                    {data?.not_yet_due_invoices && data?.not_yet_due_invoices.length > 0 ?
                        (<InvoiceListSection
                            title="Due later"
                            isGroup={data?.customer?.type && data?.customer?.type === 'group'}
                            invoices={data?.not_yet_due_invoices || []}
                            checkboxAttr='due-later'
                            selectedInvoices={selectedDueLaterInvoices}
                            onCheck={() => setSelectedDueLaterInvoices({...selectedDueLaterInvoices})}
                        />) :
                        (<></>)}
                    <Box pad={{vertical: 'small'}}>
                        <Button
                            event={{section: 'statement_of_account_page', element: 'show_all_closed_invoices'}}
                            label={(<Text fontWeight={400}>Show all closed invoices</Text>)}
                            fill="horizontal"
                            secondary
                            onClick={() => {
                                if (typeof window !== 'undefined') {
                                    document.getElementById("merchant-header").scrollIntoView({behavior: 'smooth'}); // reset scroll position so all closed invoices can be shown
                                }
                                setIsShowingPastInvoice(true);
                            }}/>
                    </Box>
                </Box>
            </Box>
        );
    }

    return (
        <ResponsiveContext.Consumer>
            {(size) => (
                <Box pad={size === "small" ? {vertical: 'medium'} : {}}>
                    {renderInvoiceSelectionList()}
                </Box>
            )}
        </ResponsiveContext.Consumer>
    );
}

const BulkPaymentWidget = ({
    selectedInvoices,
    uuid,
    recipientEmailAddress,
    isCheckout,
    setIsCheckout,
    checkoutTotal,
    currency,
    merchant,
    onReturnToMainMenu
}) => {
    const notificationCtx = useContext(NotificationsContext);
    const [checkoutSuccessData, setCheckoutSuccessData] = useState();

    const fetcher = (url) =>
        fetch(url, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
            },
        })
            .then(handleErrors)
            .catch(notificationCtx.showNotification);

    const {data: paymentMethods, error} = useSWR(
        uuid ? `/api/checkout?checkout_uuid=${uuid}` : null,
        fetcher,
        { revalidateOnFocus: false }
    );

    return isCheckout ?
        (<Box>
            <Checkout
                invoiceTokens={(selectedInvoices || []).map(invoice => invoice?.invoice_token)}
                invoiceData={(selectedInvoices || [])[0]} // TODO: delete this - just a placeholder
                isBulkPay={true}
                paymentMethods={paymentMethods}
                requireQuickPayOnCheckout={paymentMethods?.require_quick_pay_on_checkout}
                optionalQuickPayOnCheckout={paymentMethods?.optional_quick_pay_on_checkout}
                uuid={uuid}
                recipientEmailAddress={recipientEmailAddress}
                // onBack={() => setIsCheckout(false)}
                onReturnToMainMenu={onReturnToMainMenu}
                balance={{currency, amount: checkoutTotal}} //TODO: add pending balance to here when we have
                onPaymentSuccess={data => {
                    setCheckoutSuccessData(data);
                }}
                printOptions={{
                    companyHeader: (<MerchantHeader data={merchant} />),
                    data: {currency, amount: checkoutTotal}
                }}
            />
        </Box>) :
        (
            <Box
                pad="medium"
                elevation="small"
                border={true}
                margin={{top: 'small'}}
                round="xsmall"
            >
                {(selectedInvoices || []).length} invoice{(selectedInvoices || []).length > 1 ? 's' : ''} selected
                <Box direction="row" justify="between" align="center" margin={{vertical: 'small'}}>
                    <Text weight="bold" size="large">Total</Text>
                    <Text weight="bold" size="large">{formatCurrency(checkoutTotal, currency)}</Text>
                </Box>
                <Button
                    event={{section: 'statement_of_account_page', element: 'pay_now'}}
                    label="Pay now"
                    fill="horizontal"
                    primary
                    disabled={(selectedInvoices || []).length === 0 || checkoutTotal <= 0}
                    onClick={() => {
                        setIsCheckout(true);
                    }}/>
            </Box>
        );
}

const InvoiceListSection = ({
    title,
    checkboxAttr,
    invoices,
    isGroup = false,
    selectedInvoices,
    onCheck,
    navigationLink,
    isClosedInvoices=false,
    isCheckoutSummary=false,
    hideDate=false,
    wrapperProps,
    setIsCheckout,
    hasMoreClosedInvoices=false,
    onLoadMore,
    isLoadingMore=false,
    totalClosedInvoices=0
}) => {
    const [refresh, setRefresh] = useState(false);
    const theme = useContext(ThemeContext);
    const size = useContext(ResponsiveContext);
    const isMobile = size === 'small';

    // The tokens this section actually shows. Scoping to these (rather than every key in
    // selectedInvoices) keeps "Select all" honest when the map holds tokens the section
    // isn't rendering.
    const selectableTokens = (invoices || []).map(invoice => invoice?.invoice_token).filter(Boolean);
    /*
        Derived every render rather than held in useState. As state it was computed once at
        mount and only updated inside the two handlers, so it desynchronised whenever
        selectedInvoices changed from anywhere else — the statement SWR's onSuccess
        repopulating the maps on a revalidation, or a mount served from SWR cache before
        onSuccess had run. Once it drifted to false while the map was all-true, "Select all"
        computed !false and re-selected everything, so unchecking it did nothing.

        Also always a boolean now. The old reduce seeded its accumulator with 1 and returned
        that number for an empty map, which is what Grommet's CheckBox was warning about.
    */
    const allChecked = selectableTokens.length > 0
        && selectableTokens.every(token => !!selectedInvoices?.[token]);
    const getInvoiceOverdueBadge = (invoice) => {
        if (isClosedInvoices || hideDate) return null;
        const overdueRange = invoice?.due_date ? getOverdueRange(new Date(invoice.due_date)) : null;
        if (!overdueRange) return null;
        const color = OVERDUE_COLOR_MAP[overdueRange.range] || 'invoice-overdue';
        return (
            <Badge background={{ color: normalizeColor(theme.global.colors[color], theme) }}>
                {overdueRange.label}
            </Badge>
        );
    };

    const displayDate = (invoice) => {
        if (hideDate) return '';
        if (isClosedInvoices) {
            if (invoice?.is_pending_close) {
                return "Payment is being processed"
            }
            if (invoice?.invoice_closed_at) {
                return `Paid on ${formatDate(invoice.invoice_closed_at)}`
            }
            return;
        }
        return `Due on ${formatDate(invoice?.due_date)}`;
    }

    const displaySecondaryLine = (invoice) => {
        let items = []
        if (isGroup) {
            items.push(invoice.recipient.display_name)
        }
        items.push(displayDate(invoice))
        return items.join(' • ')
    }

    const renderInvoiceItem = (invoice) => {
        const { invoice_token: token } = invoice;
        return (
            <Box
                key={invoice.id || token}
                border={true}
                elevation="small"
                margin={{vertical: 'xsmall'}}
                pad={{vertical: 'small', horizontal: 'medium'}}
                direction="row"
                align="center"
                round="xsmall">
                <Box direction="row" align="center" fill="horizontal">
                    {onCheck && (
                        <Box pad={{right: 'small'}}>
                            <CheckBox
                                type="checkbox"
                                name={`select-all-${checkboxAttr}`}
                                checked={selectedInvoices[token]}
                                onClick={() => {
                                    const checked = selectedInvoices[token];
                                    selectedInvoices[token] = !checked;
                                    setRefresh(!refresh);
                                    onCheck();
                                }}/>
                        </Box>
                    )}
                    <Box justify="between" direction="row" fill="horizontal" align="center">
                        <Box pad="xsmall" flex>
                            <Box direction="row" gap="small" align="center">
                                <Text weight="bold">{`Invoice ${invoice?.order?.identifier || ''}`}</Text>
                                {!isCheckoutSummary && (
                                    <Button
                                        event={{section: 'statement_of_account_page', element: 'open_invoice_in_new_tab'}}
                                        hoverIndicator
                                        label={(<FontAwesomeIcon SelectedIcon={IconExpand} size="xsmall"/>)}
                                        plain
                                        tip="Open in new tab"
                                        onClick={() => window.open(`/p/${token}`, '_blank')}/>)}
                            </Box>
                            <Text>{displaySecondaryLine(invoice)}</Text>
                            {isMobile && (isClosedInvoices || getInvoiceOverdueBadge(invoice)) && (
                                <Box direction="row" gap="xsmall" margin={{top: 'xxsmall'}}>
                                    {isClosedInvoices && (<Badge>Paid</Badge>)}
                                    {getInvoiceOverdueBadge(invoice)}
                                </Box>
                            )}
                        </Box>
                        <Box direction="row" align="center" gap="small" flex={false}>
                            {!isMobile && isClosedInvoices && (<Badge>Paid</Badge>)}
                            {!isMobile && getInvoiceOverdueBadge(invoice)}
                            <Text size="large" textAlign="end">{formatCurrency(isClosedInvoices ? invoice?.amount : (Math.max(0, invoice?.open_balance - (invoice?.total_pending_payment || 0))), invoice?.currency)}</Text>
                        </Box>
                    </Box>
                </Box>
            </Box>
        );
    };

    return (
        <Box {...wrapperProps}>
            <Box
                align="center"
                justify="between"
                direction="row"
            >
                <Box direction="row" gap="xsmall">
                    {isCheckoutSummary ? (
                        <Button
                            label={
                                <Box pad="xsmall" round="xsmall">
                                    <FontAwesomeIcon SelectedIcon={IconChevronLeft} size="small" />
                                </Box>}
                            plain
                            hoverIndicator
                            onClick={() => setIsCheckout(false)} />
                    ) : ''}
                    {title && (
                        <Box pad={{vertical: 'xsmall'}}>
                            <Text size="large" weight="bold">{title}</Text>
                        </Box>
                    )}
                </Box>
                {navigationLink}
                {onCheck &&
                    (<Box direction="row" align="center">
                        <CheckBox
                            name={`select-all-${checkboxAttr}`}
                            checked={allChecked}
                            label="Select all"
                            onChange={() => {
                                const nextValue = !allChecked;
                                selectableTokens.forEach(token => {
                                    selectedInvoices[token] = nextValue;
                                });
                                setRefresh(!refresh);
                                onCheck();
                            }}/>
                    </Box>)
                }
            </Box>
            <Box pad={{vertical: 'xsmall'}}>
                {isClosedInvoices && (!invoices || invoices.length === 0) && (
                    <Box margin={{vertical: 'small'}} fill="horizontal" align="center">
                        <Text>No invoice is available.</Text>
                    </Box>
                )}
                {isClosedInvoices && onLoadMore ? (
                    <>
                        <InfiniteScroll
                            items={invoices}
                            step={PAGE_SIZE}
                            onMore={onLoadMore}
                        >
                            {(invoice) => renderInvoiceItem(invoice)}
                        </InfiniteScroll>
                        {isLoadingMore && (
                            <Box pad="medium" align="center">
                                <Loading />
                            </Box>
                        )}
                        {!hasMoreClosedInvoices && invoices.length > 0 && (
                            <Box pad="medium" align="center">
                                <Text size="small" color="text-weak">
                                    Showing all {totalClosedInvoices} closed invoices
                                </Text>
                            </Box>
                        )}
                    </>
                ) : (
                    <List data={invoices} pad="0" margin="0">
                        {(invoice) => renderInvoiceItem(invoice)}
                    </List>
                )}
            </Box>
        </Box>
    )
}

export const BalanceDisplay = ({number, label, size}) => (
    <Box pad={{horizontal: size !== "small" ? 'small' : 'none'}}>
        <Text size={size === "small" ? "large" : "xlarge"} weight="bold">{number}</Text>
        <Text>{label}</Text>
    </Box>
);

const CustomerSummaryHeader = ({data, printRef, setShowAddPaymentModal, uuid}) => {
    const {
        due_now: dueNow = {},
        due_later: dueLater = {},
        company_name: companyName,
        type: customerType,
        total_available_credit: totalAvailableCredit
    } = data || {};

    const [isClient, setIsClient] = useState(false);
    const [downloading, setDownloading] = useState(false);

    useEffect(() => {
        setIsClient(true);
    }, []);

    const reactToPrintFn = useReactToPrint({
        content: () => printRef?.current,
    });

    const handlePrint = () => {
        if (typeof window !== 'undefined' && reactToPrintFn) {
            reactToPrintFn();
        }
    };

    const handleDownload = (format) => {
        const url = `/api/statement/${uuid}/${format}${customerType === 'group' ? '?type=group' : ''}`;
        setDownloading(true);
        fetch(url)
            .then(async (response) => {
                const blob = await handleFileDownloadErrors(response);
                const contentDisposition = response.headers.get('Content-Disposition');
                const match = contentDisposition?.match(/filename="?([^";\n]+)"?/);
                const filename = match?.[1] || `statement.${format}`;
                return { blob, filename };
            })
            .then(({ blob, filename }) => {
                downloadBlobAsCsvFile(blob, filename);
                setDownloading(false);
            })
            .catch((error) => {
                console.error(`${format.toUpperCase()} download failed:`, error);
                setDownloading(false);
            });
    };

    const baseItems = [
        {
            label: <Text weight={400}>Download PDF</Text>,
            onClick: () => handleDownload('pdf'),
            disabled: downloading,
            event: {section: 'account_statement', element: 'download_pdf'}
        },
        {
            label: <Text weight={400}>Download CSV</Text>,
            onClick: () => handleDownload('csv'),
            disabled: downloading,
            event: {section: 'account_statement', element: 'download_csv'}
        },
        {
            label: <Text weight={400}>Print statement</Text>,
            onClick: handlePrint,
            event: {section: 'account_statement', element: 'print_statement'}
        }
    ];

    const [quickPayItem, setQuickPayItem] = useState(null);

    useEffect(() => {
        // Skip if data is not available
        if (!data) return;

        if (customerType !== 'group' && (data?.card_payment_allowed || data?.bank_payment_allowed)) {
            setQuickPayItem({
                label: <Text weight={400}>{data?.quick_pay_enrolled ? "Edit QuickPay preferences" : "Set up QuickPay"}</Text>,
                onClick: () => setShowAddPaymentModal(true),
                event: {section: 'statement_of_account', element: data?.quick_pay_enrolled ? 'edit_quick_pay' : 'set_up_quick_pay'}
            });
        }
    }, [data]);

    /*
        Every hook belongs above this line. `data` arrives from SWR, so this renders once
        without one; returning above the hooks took the hook count from none to six the
        moment the statement loaded.
    */
    if (!data) {
        return null;
    }

    const items = quickPayItem ? [...baseItems, quickPayItem] : baseItems;
    return (
        <ResponsiveContext.Consumer>
            {(size) => (
                <Box
                    align="center"
                    justify="between"
                    direction="row-responsive"
                    margin={{ top: size === "small" ? "large" : "small" }}
                    gap="medium"
                    pad={{ top: "large", bottom: "medium" }}
                >
                    <Box>
                        <Heading level={1} size="xlarge" weight="bold" margin={{ vertical: "small" }}>
                            {companyName || formatName(data, true)}
                        </Heading>
                        <Box pad={{ vertical: size === "small" ? "xsmall" : "none" }}>
                            <Text>Account statement</Text>
                        </Box>
                    </Box>
                    <Box
                        direction="row"
                        gap="medium"
                        justify="between"
                        margin={{ vertical: size === "small" ? "small" : "none" }}
                    >
                        {totalAvailableCredit ? (
                            <BalanceDisplay
                                size={size}
                                number={formatCurrency(totalAvailableCredit, dueNow?.currency || 'USD')}
                                label="Available Credit"
                            />
                        ) : (
                            <></>
                        )}
                        <BalanceDisplay
                            size={size}
                            number={formatCurrency(dueNow?.amount || 0, dueNow?.currency || 'USD')}
                            label="Due now"
                        />
                        <BalanceDisplay
                            size={size}
                            number={formatCurrency(dueLater?.amount || 0, dueLater?.currency || 'USD')}
                            label="Due later"
                        />
                        {printRef && (
                            <Menu
                                event={{section: 'statement_of_account', element: 'action_menu'}}
                                dropProps={{
                                    border: true,
                                    align: { top: "bottom", right: "right" },
                                    margin: { vertical: "xsmall", horizontal: "xsmall" },
                                }}
                                label={<FontAwesomeIcon SelectedIcon={faEllipsisStrokeVertical} />}
                                items={items}
                                tip="More actions"
                                icon={false}
                            />
                        )}
                    </Box>
                </Box>
            )}
        </ResponsiveContext.Consumer>
    );
}

export default Statement;

Statement.auth = false;
